// Hub events data access. The ONLY place that talks to the `hub_events` table.

import { supabase } from "../../lib/supabase";
import type {
  CustomerEventShare,
  HubEvent,
  HubEventInput,
  HubEventMode,
  HubEventType,
  RedistributeShare,
} from "../../lib/types";

interface HubEventRow {
  id: string;
  account_id: string;
  hub_id: string;
  type: string;
  start_date: string;
  end_date: string;
  capacity_pct: number | null;
  mode: string | null;
  customer_overrides: unknown; // jsonb -> CustomerEventShare[] (targeted customers + share)
  redistribute_volume: boolean;
  redistribute_hubs: unknown; // jsonb -> RedistributeShare[]
  active: boolean;
  notes: string | null;
}

export function rowToHubEvent(row: HubEventRow): HubEvent {
  return {
    id: row.id,
    hubId: row.hub_id,
    type: row.type as HubEventType,
    startDate: row.start_date,
    endDate: row.end_date,
    capacityPct: row.capacity_pct,
    mode: (row.mode as HubEventMode) === "by_customer" ? "by_customer" : "flat",
    customerOverrides: Array.isArray(row.customer_overrides)
      ? (row.customer_overrides as CustomerEventShare[])
      : undefined,
    redistributeVolume: row.redistribute_volume,
    redistributeHubs: Array.isArray(row.redistribute_hubs)
      ? (row.redistribute_hubs as RedistributeShare[])
      : undefined,
    active: row.active,
    notes: row.notes ?? undefined,
  };
}

export function hubEventInputToRow(accountId: string, e: HubEventInput) {
  const byCustomer = e.mode === "by_customer";
  return {
    account_id: accountId,
    hub_id: e.hubId,
    type: e.type,
    start_date: e.startDate,
    end_date: e.endDate,
    capacity_pct: e.capacityPct ?? null,
    mode: byCustomer ? "by_customer" : "flat",
    customer_overrides: byCustomer ? (e.customerOverrides ?? []) : null,
    redistribute_volume: e.redistributeVolume,
    redistribute_hubs: e.redistributeVolume ? (e.redistributeHubs ?? []) : null,
    active: e.active,
    notes: e.notes?.trim() || null,
  };
}

export async function listHubEvents(accountId: string): Promise<HubEvent[]> {
  const { data, error } = await supabase
    .from("hub_events")
    .select("*")
    .eq("account_id", accountId)
    .order("start_date", { ascending: false });
  if (error) throw error;
  return (data as HubEventRow[]).map(rowToHubEvent);
}

export async function createHubEvent(accountId: string, e: HubEventInput): Promise<void> {
  const { error } = await supabase.from("hub_events").insert(hubEventInputToRow(accountId, e));
  if (error) throw error;
}

export async function updateHubEvent(
  accountId: string,
  id: string,
  e: HubEventInput
): Promise<void> {
  const { error } = await supabase
    .from("hub_events")
    .update(hubEventInputToRow(accountId, e))
    .eq("id", id)
    .eq("account_id", accountId);
  if (error) throw error;
}

export async function deleteHubEvent(accountId: string, id: string): Promise<void> {
  const { error } = await supabase
    .from("hub_events")
    .delete()
    .eq("id", id)
    .eq("account_id", accountId);
  if (error) throw error;
}
